// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * DOM layer: builds the app shell, owns the modal + toast systems, and renders
 * the progress and result surfaces. Heavy logic (worker, image maths) lives
 * elsewhere — this module only wires what the user touches.
 */

import { icon } from './icons';
import { MODELS } from './upscale';
import type { ProgressEvent, Scale, UpscaleResult } from './types';
import { formatBytes, formatDimensions, formatDuration, megapixels } from './format';

export interface Refs {
  dropzone: HTMLElement;
  fileInput: HTMLInputElement;
  dzEmpty: HTMLElement;
  sampleBtn: HTMLButtonElement;
  dzPreview: HTMLElement;
  dzThumb: HTMLImageElement;
  dzMeta: HTMLElement;
  scaleGrid: HTMLElement;
  upscaleBtn: HTMLButtonElement;
  clearBtn: HTMLButtonElement;
  progress: HTMLElement;
  progressLabel: HTMLElement;
  progressBar: HTMLElement;
  progressDetail: HTMLElement;
  result: HTMLElement;
  compareMount: HTMLElement;
  resultStats: HTMLElement;
  resultNote: HTMLElement;
  downloadBtn: HTMLButtonElement;
  saveBtn: HTMLButtonElement;
  copyBtn: HTMLButtonElement;
  shareBtn: HTMLButtonElement;
  againBtn: HTMLButtonElement;
  drawer: HTMLElement;
  drawerToggle: HTMLButtonElement;
}

function scaleCard(s: Scale, selected: boolean): string {
  const m = MODELS[s];
  return `
    <button type="button" class="scale-card${selected ? ' active' : ''}" data-scale="${s}" role="radio" aria-checked="${selected}">
      <span class="scale-title">${m.label}</span>
      <span class="scale-blurb">${m.blurb}</span>
      <span class="scale-size">~${m.sizeMB} MB · downloads once, then works offline</span>
    </button>`;
}

const SHELL = `
<div class="app-shell">
  <header class="topbar">
    <a class="brand" href="/" aria-label="Embiggen home">
      <svg class="brand-mark" viewBox="0 0 32 32" aria-hidden="true">
        <rect x="6" y="6" width="8" height="8" rx="1.5" fill="currentColor" opacity="0.35"/>
        <path d="M14 22h8v-8" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M22 14l-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span class="brand-name">Em<span class="brand-accent">biggen</span></span>
    </a>
    <nav class="topbar-nav">
      <button type="button" class="nav-link" data-modal="how">How it works</button>
      <button type="button" class="nav-link" data-modal="privacy">Privacy</button>
      <button type="button" class="nav-link" data-modal="about">About</button>
      <button type="button" class="nav-link icon-only" id="drawer-toggle" aria-label="Open event log" title="Event log">${icon('list')}</button>
    </nav>
  </header>

  <main class="main-content">
    <button type="button" class="trust-banner" id="hero" data-modal="privacy">
      <span class="trust-dot"></span>
      Runs entirely in your browser. Your image never leaves this device.
    </button>

    <h1 class="lede">Make small images big.</h1>
    <p class="sub">Enlarge and sharpen a low-resolution image with an on-device
      <span class="glossary-link" data-term="super-resolution">super-resolution</span> model —
      far beyond what a plain <span class="glossary-link" data-term="bicubic">bicubic</span> resize can do.
      No upload, no account, no watermark.</p>

    <section class="workbench">
      <div class="dropzone" id="dropzone" tabindex="0" role="button" aria-label="Choose an image to upscale">
        <div class="dropzone-empty" id="dz-empty">
          ${icon('image', 34)}
          <p class="dz-title">Drop an image, tap to pick, or paste</p>
          <p class="dz-sub">JPEG, PNG, WebP, GIF, BMP — up to a 4096&nbsp;px result.</p>
          <button type="button" class="sample-link" id="sample-btn">${icon('sparkles', 14)} Try a sample image</button>
        </div>
        <div class="dropzone-preview" id="dz-preview" hidden>
          <img id="dz-thumb" alt="Selected image preview" />
          <div class="dz-info">
            <p class="dz-meta" id="dz-meta"></p>
            <p class="dz-swap">Drop or tap to choose a different image</p>
          </div>
        </div>
        <input type="file" id="file-input" accept="image/*" hidden />
      </div>

      <div class="controls">
        <div class="scale-grid" id="scale-grid" role="radiogroup" aria-label="Upscale factor">
          ${scaleCard(2, true)}
          ${scaleCard(4, false)}
        </div>
        <div class="action-row">
          <button type="button" class="btn primary" id="upscale-btn" disabled>${icon('sparkles')} Upscale</button>
          <button type="button" class="btn ghost" id="clear-btn" disabled>${icon('trash')} Clear</button>
        </div>
      </div>

      <div class="progress-card" id="progress" hidden aria-live="polite">
        <div class="progress-top">
          <span class="progress-label" id="progress-label">Working…</span>
          <span class="progress-detail" id="progress-detail"></span>
        </div>
        <div class="progress-track"><div class="progress-bar" id="progress-bar"></div></div>
      </div>

      <section class="result" id="result" hidden>
        <div class="compare-mount" id="compare-mount"></div>
        <p class="result-stats" id="result-stats"></p>
        <p class="result-note" id="result-note" hidden></p>
        <div class="deliver-row">
          <button type="button" class="btn primary" id="download-btn">${icon('download')} Download PNG</button>
          <button type="button" class="btn ghost" id="save-btn" hidden>${icon('save')} Save as…</button>
          <button type="button" class="btn ghost" id="copy-btn" hidden>${icon('copy')} Copy</button>
          <button type="button" class="btn ghost" id="share-btn" hidden>${icon('share')} Share</button>
          <button type="button" class="btn ghost" id="again-btn">${icon('refresh')} New image</button>
        </div>
      </section>
    </section>
  </main>

  <footer class="site-footer">
    <div class="footer-inner">
      Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
    </div>
  </footer>

  <aside id="event-drawer" hidden></aside>
  <div id="modal-host" hidden></div>
  <div id="toast-area" aria-live="polite"></div>
</div>
`;

export function buildShell(root: HTMLElement): Refs {
  root.innerHTML = SHELL;
  const $ = <T extends HTMLElement>(sel: string): T => root.querySelector(sel) as T;
  return {
    dropzone: $('#dropzone'),
    fileInput: $('#file-input'),
    dzEmpty: $('#dz-empty'),
    sampleBtn: $('#sample-btn'),
    dzPreview: $('#dz-preview'),
    dzThumb: $('#dz-thumb'),
    dzMeta: $('#dz-meta'),
    scaleGrid: $('#scale-grid'),
    upscaleBtn: $('#upscale-btn'),
    clearBtn: $('#clear-btn'),
    progress: $('#progress'),
    progressLabel: $('#progress-label'),
    progressBar: $('#progress-bar'),
    progressDetail: $('#progress-detail'),
    result: $('#result'),
    compareMount: $('#compare-mount'),
    resultStats: $('#result-stats'),
    resultNote: $('#result-note'),
    downloadBtn: $('#download-btn'),
    saveBtn: $('#save-btn'),
    copyBtn: $('#copy-btn'),
    shareBtn: $('#share-btn'),
    againBtn: $('#again-btn'),
    drawer: $('#event-drawer'),
    drawerToggle: $('#drawer-toggle'),
  };
}

// ── Progress ──────────────────────────────────────────────────────────────────

export function showProgress(refs: Refs, on: boolean): void {
  refs.progress.hidden = !on;
  if (on) {
    refs.progressBar.style.width = '0%';
    refs.progressBar.classList.remove('indeterminate');
  }
}

export function updateProgress(refs: Refs, e: ProgressEvent): void {
  if (e.label) refs.progressLabel.textContent = e.label;
  const indeterminate = e.fraction === undefined && e.stage === 'inferring';
  refs.progressBar.classList.toggle('indeterminate', indeterminate);
  if (typeof e.fraction === 'number') {
    refs.progressBar.style.width = `${Math.round(Math.max(0, Math.min(1, e.fraction)) * 100)}%`;
  } else if (indeterminate) {
    refs.progressBar.style.width = '100%';
  }
  if (e.bytesLoaded !== undefined && e.bytesTotal) {
    refs.progressDetail.textContent = `${formatBytes(e.bytesLoaded)} / ${formatBytes(e.bytesTotal)}`;
  } else if (e.stage === 'loading-model') {
    refs.progressDetail.textContent = 'first use of this scale';
  } else {
    refs.progressDetail.textContent = '';
  }
}

// ── Result ────────────────────────────────────────────────────────────────────

export function renderResultStats(refs: Refs, r: UpscaleResult): void {
  const dev = r.device === 'webgpu' ? 'WebGPU' : 'WASM (CPU)';
  refs.resultStats.innerHTML = `
    <strong>${formatDimensions(r.srcWidth, r.srcHeight)}</strong>
    ${icon('expand', 14)}
    <strong>${formatDimensions(r.outWidth, r.outHeight)}</strong>
    <span class="dot">·</span> ${megapixels(r.outWidth, r.outHeight)}
    <span class="dot">·</span> ${r.scale}× <span class="dot">·</span> ${dev}
    <span class="dot">·</span> ${formatDuration(r.totalMs)}`;
  const notes: string[] = [];
  if (r.downscaled) {
    notes.push(
      `Your image was larger than the model handles at once, so it was scaled to ${formatDimensions(
        r.workWidth,
        r.workHeight,
      )} before upscaling to keep the result within 4096&nbsp;px.`,
    );
  }
  notes.push('Output is an RGB PNG — any transparency in the source is flattened.');
  refs.resultNote.innerHTML = notes.join(' ');
  refs.resultNote.hidden = false;
}

// ── Modal system ──────────────────────────────────────────────────────────────

let modalHost: HTMLElement | null = null;
let onCloseHooks: Array<() => void> = [];

function host(): HTMLElement {
  if (!modalHost) modalHost = document.getElementById('modal-host');
  return modalHost as HTMLElement;
}

export function isOverlayOpen(): boolean {
  const h = host();
  const d = document.getElementById('event-drawer');
  return (h && !h.hidden) || (d ? !d.hidden : false);
}

export function openModal(title: string, body: HTMLElement | string): void {
  const h = host();
  h.innerHTML = '';
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');

  const head = document.createElement('div');
  head.className = 'modal-header';
  const h2 = document.createElement('h2');
  h2.className = 'modal-title';
  h2.textContent = title;
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'modal-close';
  close.setAttribute('aria-label', 'Close');
  close.innerHTML = icon('x');
  close.addEventListener('click', closeModal);
  head.appendChild(h2);
  head.appendChild(close);

  const content = document.createElement('div');
  content.className = 'modal-body';
  if (typeof body === 'string') content.innerHTML = body;
  else content.appendChild(body);

  modal.appendChild(head);
  modal.appendChild(content);
  backdrop.appendChild(modal);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) closeModal();
  });
  h.appendChild(backdrop);
  h.hidden = false;
}

export function closeModal(): void {
  const h = host();
  h.hidden = true;
  h.innerHTML = '';
  for (const fn of onCloseHooks) fn();
  onCloseHooks = [];
}

export function onModalClose(fn: () => void): void {
  onCloseHooks.push(fn);
}

// ── Toast ─────────────────────────────────────────────────────────────────────

export function toast(msg: string, kind: 'ok' | 'err' = 'ok'): void {
  const area = document.getElementById('toast-area');
  if (!area) return;
  const t = document.createElement('div');
  t.className = `toast ${kind}`;
  t.innerHTML = `${icon(kind === 'ok' ? 'check' : 'info')}<span>${escapeHtml(msg)}</span>`;
  area.appendChild(t);
  requestAnimationFrame(() => t.classList.add('show'));
  setTimeout(() => {
    t.classList.remove('show');
    setTimeout(() => t.remove(), 300);
  }, 2800);
}

// ── Modal content ─────────────────────────────────────────────────────────────

export function modalContent(id: string): { title: string; html: string } {
  if (id === 'how') {
    return {
      title: 'How it works',
      html: `
        <ol class="steps">
          <li><span class="step-num">1</span><div><h3>Drop an image</h3>
            <p>Pick any raster image. It stays in this browser tab — it is never uploaded.</p></div></li>
          <li><span class="step-num">2</span><div><h3>Choose 2× or 4×</h3>
            <p>The first time you use a scale, your browser downloads that
            <span class="glossary-link" data-term="swin2sr">Swin2SR</span> model once (~50&nbsp;MB for 2×,
            ~130&nbsp;MB for 4×) from Hugging Face, then caches it for offline use.</p></div></li>
          <li><span class="step-num">3</span><div><h3>Run on-device inference</h3>
            <p>A Web Worker runs the <span class="glossary-link" data-term="super-resolution">super-resolution</span>
            model on <span class="glossary-link" data-term="webgpu">WebGPU</span> — or
            <span class="glossary-link" data-term="wasm">WASM</span> on the CPU if WebGPU is unavailable —
            reconstructing detail a plain resize can't.</p></div></li>
          <li><span class="step-num">4</span><div><h3>Compare and save</h3>
            <p>Drag the slider to see before vs after, then download, copy, share, or Save as… the
            upscaled PNG.</p></div></li>
        </ol>`,
    };
  }
  if (id === 'privacy') {
    return {
      title: 'Privacy & threat model',
      html: `
        <div class="security-grid">
          <div class="security-card good">
            <h3>What is protected</h3>
            <ul>
              <li>Your image. It is decoded, upscaled and delivered entirely in this tab — never uploaded.</li>
              <li>The result PNG. Generated locally, saved locally.</li>
              <li>No accounts, no cookies, no third-party fonts, no watermark, no telemetry of your files.</li>
            </ul>
          </div>
          <div class="security-card warn">
            <h3>What is <em>not</em> fully private</h3>
            <ul>
              <li>The <strong>first time</strong> you use a given scale, your browser fetches that
                <span class="glossary-link" data-term="onnx">ONNX</span> model (~50–130&nbsp;MB) from
                <code>huggingface.co</code>. Hugging Face learns an IP fetched the model — but never sees your image.
                Later uses are fully offline.</li>
              <li>GitHub Pages and Cloudflare see ordinary page-load metadata, as any website visit does.</li>
              <li>A Cloudflare Web Analytics beacon records an anonymous page view — no cookies, no fingerprinting,
                no cross-site tracking; your image is never sent to it.</li>
              <li>Feedback you choose to send goes to <code>feedback.benrichardson.dev</code>. Nothing is sent
                unless you open the feedback form and press Send; your image never is.</li>
            </ul>
          </div>
          <div class="security-card">
            <h3>Trust surface</h3>
            <ul>
              <li>This static site bundle — built and deployed by GitHub Actions, pinned by commit hash.</li>
              <li>The TLS chain to <code>embiggen.benrichardson.dev</code> (Cloudflare DNS → GitHub Pages).</li>
              <li>The Swin2SR <span class="glossary-link" data-term="onnx">ONNX</span> weights fetched once from Hugging Face.</li>
              <li>The transformers.js runtime and its bundled ONNX Runtime WASM (both Apache&nbsp;2.0).</li>
            </ul>
          </div>
        </div>`,
    };
  }
  return {
    title: 'About Embiggen',
    html: `
      <p>Embiggen is a tiny browser tool that enlarges and sharpens images locally, using an
        open-source <span class="glossary-link" data-term="super-resolution">super-resolution</span> model
        running on WebGPU (or WASM). There is no backend, no upload, no quota, and no account — just one HTML
        page, one JavaScript bundle, and a Web Worker.</p>
      <p>The models are
        <a href="https://huggingface.co/Xenova/swin2SR-classical-sr-x2-64" target="_blank" rel="noopener">Swin2SR</a>
        conversions run via
        <a href="https://huggingface.co/docs/transformers.js" target="_blank" rel="noopener">transformers.js</a>.
        Swin2SR reconstructs faithful detail (tuned for high <span class="glossary-link" data-term="psnr">PSNR</span>),
        it does not invent faces or textures.</p>
      <p class="about-meta">
        Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
        · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
        · <a href="https://github.com/ben-gy/embiggen" target="_blank" rel="noopener">source on GitHub</a>
      </p>`,
  };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      default: return '&#39;';
    }
  });
}
